import type { Collection } from "@/lib/client/favorites";
import { favoritesApi } from "@/lib/client/favorites";
import { tokenService } from "@/lib/client/tokenService";
import { logError } from "@/lib/client/errors";

export interface OptimisticAddResult {
  success: boolean;
  error?: string;
  isCollectionFull?: boolean;
  favoriteId?: number;
}

export interface OptimisticRemoveResult {
  success: boolean;
  error?: string;
}

export interface CollectionItemsResult {
  success: boolean;
  items: SearchResultItem[];
  allItemIds: Set<number>;
}

export class CollectionsError extends Error {
  constructor(
    message: string,
    public code?: string
  ) {
    super(message);
    this.name = "CollectionsError";
  }
}

/**
 * Sorts collections by their collections_order property.
 * Collections without an order are placed at the end.
 * Does not mutate the original array.
 *
 * @param collections - Array of collections to sort
 * @returns New array sorted by collections_order (ascending)
 *
 * @example
 * ```ts
 * const collections = [
 *   { id: 1, collections_order: 3 },
 *   { id: 2, collections_order: 1 }
 * ];
 * const sorted = sortCollectionsByOrder(collections);
 * // sorted[0].id === 2, sorted[1].id === 1
 * ```
 */
export const sortCollectionsByOrder = (collections: Collection[]): Collection[] => {
  return [...collections].sort((a, b) => {
    const orderA = a.collections_order ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.collections_order ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });
};

/**
 * Checks if an item exists in collections other than the specified one.
 * Used to determine if an item should be removed from the global item cache
 * when removed from a specific collection.
 *
 * @param collectionId - The collection to exclude from check (null checks all)
 * @param collectionItemsCache - Map of collection ID to items
 * @param itemId - The item ID to search for
 * @returns true if item exists in other collections, false otherwise
 */
export const isItemInOtherCollections = (
  collectionId: number | null,
  collectionItemsCache: Record<number, SearchResultItem[]>,
  itemId: number
): boolean => {
  return Object.entries(collectionItemsCache).some(([cachedCollectionId, items]) => {
    if (parseInt(cachedCollectionId) === collectionId) return false;
    return items.some((item) => item.id === itemId);
  });
};

/**
 * Determines the appropriate collection ID to use for operations.
 * Returns the active collection if set, otherwise falls back to the first collection.
 *
 * @param activeCollectionId - The currently active collection ID
 * @param collections - Array of available collections
 * @returns A collection ID or null if no collections exist
 */
export const getDefaultCollectionId = (
  activeCollectionId: number | null,
  collections: Collection[]
): number | null => {
  return activeCollectionId || collections[0]?.id || null;
};

/**
 * Checks if a collection with the given ID exists in the collections array.
 *
 * @param collections - Array of collections to search
 * @param collectionId - The collection ID to check for
 * @returns true if collection exists, false otherwise
 */
export const collectionExists = (
  collections: Collection[],
  collectionId: number | null
): boolean => {
  if (!collectionId) return false;
  return collections.some((c) => c.id === collectionId);
};

export const collectionsService = {
  /**
   * Loads all collections for the authenticated user.
   * Handles multiple response formats from the API (array, object with data property, etc.).
   *
   * @returns Promise resolving to array of collections
   * @throws {CollectionsError} If API call fails or returns invalid data
   */
  async loadCollections(): Promise<Collection[]> {
    const token = await tokenService.getToken();
    const result = await favoritesApi.getUserCollections(token);

    if (!result.success || !result.data) {
      throw new CollectionsError(result.error || "Failed to load collections");
    }

    const collectionsData = Array.isArray(result.data)
      ? result.data
      : (result.data as Record<string, unknown>).data ||
        (result.data as Record<string, unknown>).collections ||
        [];

    return collectionsData as Collection[];
  },

  /**
   * Loads items for a specific collection with pagination.
   *
   * @param collectionId - The collection ID to load items from
   * @param limit - Maximum number of items to return (default: 1000)
   * @param offset - Number of items to skip (default: 0)
   * @returns Promise resolving to items and a Set of all item IDs
   * @throws {CollectionsError} If API call fails
   */
  async loadCollectionItems(
    collectionId: number,
    limit: number = 1000,
    offset: number = 0
  ): Promise<CollectionItemsResult> {
    const token = await tokenService.getToken();
    const result = await favoritesApi.getCollectionItems(token, collectionId, limit, offset);

    if (!result.success || !result.data) {
      throw new CollectionsError(result.error || "Failed to load collection items");
    }

    const items = Array.isArray(result.data) ? (result.data as SearchResultItem[]) : [];
    const allItemIds = new Set(items.map((item) => item.id));

    return { success: true, items, allItemIds };
  },

  /**
   * Loads items for a public collection (no authentication required).
   *
   * @param collectionId - The public collection ID to load items from
   * @param limit - Maximum number of items to return (default: 1000)
   * @param offset - Number of items to skip (default: 0)
   * @returns Promise resolving to items and a Set of all item IDs
   * @throws {CollectionsError} If API call fails
   */
  async loadPublicCollectionItems(
    collectionId: number,
    limit: number = 1000,
    offset: number = 0
  ): Promise<CollectionItemsResult> {
    const result = await favoritesApi.getPublicCollectionItems(collectionId, limit, offset);

    if (!result.success || !result.data) {
      throw new CollectionsError(result.error || "Failed to load public collection items");
    }

    const items = Array.isArray(result.data) ? (result.data as SearchResultItem[]) : [];
    const allItemIds = new Set(items.map((item) => item.id));

    return { success: true, items, allItemIds };
  },

  /**
   * Adds an item to a collection.
   *
   * @param itemId - The ID of the item to add
   * @param collectionId - The ID of the collection to add to
   * @returns Promise resolving to success status and optional error/code
   */
  async addItemToCollection(
    itemId: number,
    collectionId: number
  ): Promise<{ success: boolean; error?: string; code?: string; favoriteId?: number }> {
    const token = await tokenService.getToken();
    const result = await favoritesApi.addItemToCollection(token, itemId, collectionId);

    if (!result.success) {
      const isNetworkError =
        result.error?.startsWith("Failed to fetch") ||
        result.error?.startsWith("Load failed") ||
        result.error?.startsWith("NetworkError");
      return {
        success: false,
        error: isNetworkError
          ? "Please try again."
          : result.error || "Failed to add item to favorites",
        code: result.code,
      };
    }

    const favoriteId = (result.data as { id?: number } | undefined)?.id;
    return { success: true, favoriteId };
  },

  /**
   * Removes an item from a collection.
   *
   * @param itemId - The ID of the item to remove
   * @param collectionId - The ID of the collection to remove from
   * @returns Promise resolving to success status and optional error message
   */
  async removeItemFromCollection(
    itemId: number,
    collectionId: number
  ): Promise<{ success: boolean; error?: string }> {
    const token = await tokenService.getToken();
    const result = await favoritesApi.removeItemFromCollection(token, itemId, collectionId);

    if (!result.success) {
      return {
        success: false,
        error: result.error || "Failed to remove item from favorites",
      };
    }

    return { success: true };
  },

  /**
   * Creates a new collection for the authenticated user.
   *
   * @param collectionName - The name/title for the new collection
   * @param options - Optional configuration (appearance, metadata)
   * @returns Promise resolving to the created collection
   * @throws {CollectionsError} If creation fails
   */
  async createCollection(
    collectionName: string,
    options?: { appearance?: number; metadata?: Record<string, unknown> }
  ): Promise<Collection> {
    const token = await tokenService.getToken();
    const result = await favoritesApi.createCollection(token, {
      collection_title: collectionName,
      appearance: options?.appearance ?? 0,
      metadata: options?.metadata,
    });

    if (!result.success || !result.data) {
      throw new CollectionsError(result.error || "Failed to create collection");
    }

    return result.data;
  },

  /**
   * Deletes a collection by ID.
   *
   * @param collectionId - The ID of the collection to delete
   * @returns Promise resolving to success status and optional error message
   */
  async deleteCollection(collectionId: number): Promise<{ success: boolean; error?: string }> {
    const token = await tokenService.getToken();
    const result = await favoritesApi.deleteCollection(token, collectionId);

    if (!result.success) {
      return {
        success: false,
        error: result.error || "Failed to delete collection",
      };
    }

    return { success: true };
  },

  /**
   * Updates a collection's properties.
   * Converts null metadata to undefined before sending to API.
   *
   * @param collectionId - The ID of the collection to update
   * @param updates - Partial collection object with fields to update
   * @returns Promise resolving to success status and optional error message
   */
  async updateCollection(
    collectionId: number,
    updates: Partial<Collection>
  ): Promise<{ success: boolean; error?: string }> {
    const token = await tokenService.getToken();
    const sanitizedUpdates = {
      ...updates,
      metadata: updates.metadata === null ? undefined : updates.metadata,
    };

    const result = await favoritesApi.updateCollection(token, collectionId, sanitizedUpdates);

    if (!result.success) {
      return {
        success: false,
        error: result.error || "Failed to update collection",
      };
    }

    return { success: true };
  },

  /**
   * Updates the order of multiple collections.
   *
   * @param orders - Array of {id, order} pairs specifying new order
   * @returns Promise resolving to success status and optional error message
   */
  async updateCollectionOrders(
    orders: Array<{ id: number; order: number }>
  ): Promise<{ success: boolean; error?: string }> {
    const token = await tokenService.getToken();
    const result = await favoritesApi.updateCollectionOrders(token, orders);

    if (!result.success) {
      return {
        success: false,
        error: result.error || "Failed to update collection orders",
      };
    }

    return { success: true };
  },

  /**
   * Fetches information about which users liked a specific item.
   *
   * @param itemId - The ID of the item to query
   * @returns Promise resolving to success status and optional data with total users and last username
   */
  async fetchItemLikedBy(
    itemId: number
  ): Promise<{ success: boolean; data?: { total_users: number; last_username: string | null } }> {
    const result = await favoritesApi.getItemLikedBy(itemId);

    if (!result.success || !result.data) {
      return { success: false };
    }

    return {
      success: true,
      data: {
        total_users: result.data.total_users,
        last_username: result.data.last_username,
      },
    };
  },

  /**
   * Preloads items for all collections except the active one.
   * Errors are logged but do not reject the promise.
   *
   * @param collections - All collections to potentially preload
   * @param activeCollectionId - The collection to skip (currently active)
   * @returns Promise resolving to cache map of collection ID to items
   */
  async preloadOtherCollections(
    collections: Collection[],
    activeCollectionId: number | null
  ): Promise<Record<number, SearchResultItem[]>> {
    const token = await tokenService.getToken();
    const cache: Record<number, SearchResultItem[]> = {};

    const otherCollections = collections.filter((c) => c.id !== activeCollectionId);

    await Promise.all(
      otherCollections.map(async (collection) => {
        try {
          const result = await favoritesApi.getCollectionItems(token, collection.id);

          if (result.success && result.data) {
            const items = Array.isArray(result.data) ? (result.data as SearchResultItem[]) : [];
            cache[collection.id] = items;
          }
        } catch (error) {
          logError(error as Error, {
            context: "preloadOtherCollections",
            collectionId: collection.id,
          });
        }
      })
    );

    return cache;
  },

  // === Business Logic ===

  /**
   * Validates if an item can be added and determines target collection.
   *
   * @param itemId - Item ID to validate
   * @param activeCollectionItemIds - Set of item IDs in active collection
   * @param collections - Available collections
   * @param activeCollectionId - Currently active collection ID
   * @returns Validation result with collection ID to use
   */
  validateAddItem(
    itemId: number,
    activeCollectionItemIds: Set<number>,
    collections: Collection[],
    activeCollectionId: number | null
  ): { valid: boolean; error?: string; collectionId?: number } {
    if (activeCollectionItemIds.has(itemId)) {
      return { valid: true };
    }

    const collectionId = getDefaultCollectionId(activeCollectionId, collections);
    if (!collectionId) {
      return { valid: false, error: "No collections available" };
    }

    return { valid: true, collectionId };
  },

  /**
   * Calculates optimistic count changes for favorite counts.
   * If item is in other collections, count is not tracked (still favorited elsewhere).
   *
   * @param optimisticCountChanges - Current map of item ID to count changes
   * @param itemId - The item being added/removed
   * @param isInOtherCollections - Whether item exists in other collections
   * @param delta - Change amount (+1 for add, -1 for remove)
   * @returns New map with updated count changes (does not mutate original)
   */
  calculateOptimisticCountChange(
    optimisticCountChanges: Map<number, number>,
    itemId: number,
    isInOtherCollections: boolean,
    delta: number
  ): Map<number, number> {
    if (isInOtherCollections) {
      return optimisticCountChanges;
    }

    const newCountChanges = new Map(optimisticCountChanges);
    const currentChange = newCountChanges.get(itemId) || 0;
    newCountChanges.set(itemId, currentChange + delta);

    return newCountChanges;
  },

  /**
   * Determines if optimistic update should be reverted based on error code.
   *
   * @param errorCode - The error code from the API response
   * @returns true if error should trigger revert, false otherwise
   */
  shouldRevertOnAddError(errorCode: string | undefined): boolean {
    return errorCode === "COLLECTION_FULL";
  },

  /**
   * Maps error codes to user-friendly messages.
   *
   * @param errorCode - The error code from the API response
   * @param defaultError - Fallback error message
   * @returns User-friendly error message
   */
  getAddErrorMessage(errorCode: string | undefined, defaultError: string): string {
    if (errorCode === "COLLECTION_FULL") {
      return "Collection full. Create a new list to keep saving.";
    }
    return defaultError;
  },

  // === Pagination Logic ===

  /**
   * Calculates the new count of items to display after "load more" action.
   *
   * @param displayedItemsCount - Current number of displayed items
   * @param totalItems - Total number of items available
   * @param increment - Number of additional items to load (default: 20)
   * @returns Object with newCount and hasMore boolean
   */
  calculateLoadMoreCount(
    displayedItemsCount: number,
    totalItems: number,
    increment: number = 20
  ): { newCount: number; hasMore: boolean } {
    const newCount = Math.min(displayedItemsCount + increment, totalItems);
    return {
      newCount,
      hasMore: newCount < totalItems,
    };
  },

  /**
   * Determines the appropriate active collection ID.
   * Returns current ID if valid, otherwise first available collection.
   *
   * @param currentActiveId - The current active collection ID
   * @param collections - Array of available collections
   * @returns A valid collection ID or null if no collections exist
   */
  determineActiveCollectionId(
    currentActiveId: number | null,
    collections: Collection[]
  ): number | null {
    if (currentActiveId && collectionExists(collections, currentActiveId)) {
      return currentActiveId;
    }

    return collections.length > 0 ? collections[0].id : null;
  },

  /**
   * Builds an updated cache entry for a collection's items.
   * Does not mutate the original cache object.
   *
   * @param existingCache - Current cache of collection items
   * @param collectionId - Collection ID to update
   * @param items - Items to store in cache
   * @returns New cache object with updated entry
   */
  buildCollectionItemsCache(
    existingCache: Record<number, SearchResultItem[]>,
    collectionId: number,
    items: SearchResultItem[]
  ): Record<number, SearchResultItem[]> {
    return {
      ...existingCache,
      [collectionId]: items,
    };
  },

  /**
   * Builds an updated map of collection item IDs.
   * Does not mutate the original map.
   *
   * @param existingMap - Current map of collection ID to item ID sets
   * @param collectionId - Collection ID to update
   * @param itemIds - Set of item IDs to store
   * @returns New map with updated entry
   */
  buildAllCollectionItemIds(
    existingMap: Map<number, Set<number>>,
    collectionId: number,
    itemIds: Set<number>
  ): Map<number, Set<number>> {
    const newMap = new Map(existingMap);
    newMap.set(collectionId, itemIds);
    return newMap;
  },

  // === Optimistic Operations ===

  /**
   * Adds an item to a collection with optimistic UI updates.
   * Applies UI changes immediately, reverts on API failure.
   *
   * @param itemId - The item ID to add
   * @param item - Full item object (optional, for immediate UI update)
   * @param getState - Function to access current store state
   * @param setState - Function to update store state
   * @param loadCollectionItems - Function to reload collection from API
   * @param fetchItemLikedBy - Function to refresh "liked by" data
   * @returns Promise resolving to success status and optional error/full status
   */
  async optimisticallyAddItem(
    itemId: number,
    item: SearchResultItem | undefined,
    getState: () => Pick<
      ReturnType<typeof import("@/stores/collectionsStore").useCollectionsStore.getState>,
      | "activeCollectionItemIds"
      | "activeCollectionItems"
      | "activeCollectionId"
      | "collectionItemsCache"
      | "allCollectionItemIds"
      | "collections"
      | "optimisticCountChanges"
    >,
    setState: (
      updates: Partial<{
        activeCollectionItemIds: Set<number>;
        activeCollectionItems: SearchResultItem[];
        optimisticCountChanges: Map<number, number>;
        collectionItemsCache: Record<number, SearchResultItem[]>;
        allCollectionItemIds: Map<number, Set<number>>;
        hasPendingChanges: boolean;
      }>
    ) => void,
    fetchItemLikedBy: (itemId: number) => Promise<void>
  ): Promise<OptimisticAddResult> {
    const state = getState();

    if (state.activeCollectionItemIds.has(itemId)) {
      return { success: true };
    }

    const collectionToUse = getDefaultCollectionId(state.activeCollectionId, state.collections);
    if (!collectionToUse) {
      return { success: false, error: "No collections available" };
    }

    // Apply optimistic update
    const newIds = new Set(state.activeCollectionItemIds);
    newIds.add(itemId);
    const newAllItemIds = this.buildAllCollectionItemIds(
      state.allCollectionItemIds,
      collectionToUse,
      newIds
    );

    const isInOtherCollections = isItemInOtherCollections(
      state.activeCollectionId,
      state.collectionItemsCache,
      itemId
    );

    const newCountChanges = this.calculateOptimisticCountChange(
      state.optimisticCountChanges,
      itemId,
      isInOtherCollections,
      1
    );

    const dedupedItems = state.activeCollectionItems.filter((i) => i.id !== itemId);
    const newItems = item ? [item, ...dedupedItems] : state.activeCollectionItems;

    // Update collection cache
    if (state.activeCollectionId && item) {
      const newCache = this.buildCollectionItemsCache(
        state.collectionItemsCache,
        state.activeCollectionId,
        newItems
      );
      setState({ collectionItemsCache: newCache });
    }

    setState({
      activeCollectionItemIds: newIds,
      activeCollectionItems: newItems,
      optimisticCountChanges: newCountChanges,
      allCollectionItemIds: newAllItemIds,
      hasPendingChanges: true,
    });

    // Make API call
    const result = await this.addItemToCollection(itemId, collectionToUse);

    if (!result.success) {
      // Revert optimistic update on error
      const revertIds = new Set(state.activeCollectionItemIds);
      const revertAllItemIds = this.buildAllCollectionItemIds(
        state.allCollectionItemIds,
        collectionToUse,
        revertIds
      );
      const revertItems = state.activeCollectionItems.filter((i) => i.id !== itemId);

      const revertCountChanges = this.calculateOptimisticCountChange(
        newCountChanges,
        itemId,
        isInOtherCollections,
        -1
      );

      if (state.activeCollectionId) {
        const revertCache = this.buildCollectionItemsCache(
          state.collectionItemsCache,
          state.activeCollectionId,
          revertItems
        );
        setState({ collectionItemsCache: revertCache });
      }

      setState({
        activeCollectionItemIds: revertIds,
        activeCollectionItems: revertItems,
        optimisticCountChanges: revertCountChanges,
        allCollectionItemIds: revertAllItemIds,
        hasPendingChanges: false,
      });

      return {
        success: false,
        error: this.getAddErrorMessage(
          result.code,
          result.error || "Failed to add item to favorites"
        ),
        isCollectionFull: result.code === "COLLECTION_FULL",
      };
    }

    // On success, update the item with the favorite_id from the API response
    if (result.favoriteId) {
      const latestState = getState();
      const updatedItems = latestState.activeCollectionItems.map((i) =>
        i.id === itemId ? { ...i, favorite_id: result.favoriteId } : i
      );
      const cacheKey = latestState.activeCollectionId;
      if (cacheKey) {
        const newCache = this.buildCollectionItemsCache(
          latestState.collectionItemsCache,
          cacheKey,
          updatedItems
        );
        setState({
          activeCollectionItems: updatedItems,
          collectionItemsCache: newCache,
        });
      }
    }

    await fetchItemLikedBy(itemId);

    return { success: true, favoriteId: result.favoriteId };
  },

  /**
   * Removes an item from a collection with optimistic UI updates.
   * Applies UI changes immediately, reverts on API failure.
   *
   * @param itemId - The item ID to remove
   * @param getState - Function to access current store state
   * @param setState - Function to update store state
   * @param loadCollectionItems - Function to reload collection from API
   * @param fetchItemLikedBy - Function to refresh "liked by" data
   * @returns Promise resolving to success status and optional error message
   */
  async optimisticallyRemoveItem(
    itemId: number,
    getState: () => Pick<
      ReturnType<typeof import("@/stores/collectionsStore").useCollectionsStore.getState>,
      | "activeCollectionItemIds"
      | "activeCollectionItems"
      | "activeCollectionId"
      | "collectionItemsCache"
      | "allCollectionItemIds"
      | "collections"
      | "optimisticCountChanges"
    >,
    setState: (
      updates: Partial<{
        activeCollectionItemIds: Set<number>;
        activeCollectionItems: SearchResultItem[];
        optimisticCountChanges: Map<number, number>;
        collectionItemsCache: Record<number, SearchResultItem[]>;
        allCollectionItemIds: Map<number, Set<number>>;
        hasPendingChanges: boolean;
      }>
    ) => void,
    loadCollectionItems: (collectionId: number, silent?: boolean) => Promise<void>,
    fetchItemLikedBy: (itemId: number) => Promise<void>
  ): Promise<OptimisticRemoveResult> {
    const state = getState();

    const collectionToUse = getDefaultCollectionId(state.activeCollectionId, state.collections);
    if (!collectionToUse) {
      return { success: false, error: "No collections available" };
    }

    const originalItem = state.activeCollectionItems.find((item) => item.id === itemId);

    const newIds = new Set(state.activeCollectionItemIds);
    newIds.delete(itemId);
    const newAllItemIds = this.buildAllCollectionItemIds(
      state.allCollectionItemIds,
      collectionToUse,
      newIds
    );

    const newItems = state.activeCollectionItems.filter((item) => item.id !== itemId);

    const isInOtherCollections = isItemInOtherCollections(
      state.activeCollectionId,
      state.collectionItemsCache,
      itemId
    );

    const newCountChanges = this.calculateOptimisticCountChange(
      state.optimisticCountChanges,
      itemId,
      isInOtherCollections,
      -1
    );

    if (state.activeCollectionId) {
      const newCache = this.buildCollectionItemsCache(
        state.collectionItemsCache,
        state.activeCollectionId,
        newItems
      );
      setState({ collectionItemsCache: newCache });
    }

    setState({
      activeCollectionItemIds: newIds,
      activeCollectionItems: newItems,
      optimisticCountChanges: newCountChanges,
      allCollectionItemIds: newAllItemIds,
      hasPendingChanges: true,
    });

    // Make API call
    try {
      const result = await this.removeItemFromCollection(itemId, collectionToUse);

      if (!result.success) {
        throw new Error(result.error);
      }

      await fetchItemLikedBy(itemId);

      return { success: true };
    } catch {
      // Revert optimistic update on error — use fresh state to avoid stale-snapshot duplicates
      const currentState = getState();
      const revertIds = new Set(currentState.activeCollectionItemIds);
      revertIds.add(itemId);
      const revertAllItemIds = this.buildAllCollectionItemIds(
        currentState.allCollectionItemIds,
        collectionToUse,
        revertIds
      );
      const alreadyPresent = currentState.activeCollectionItems.some((i) => i.id === itemId);
      const revertItems =
        originalItem && !alreadyPresent
          ? [originalItem, ...currentState.activeCollectionItems]
          : currentState.activeCollectionItems;

      const revertCountChanges = this.calculateOptimisticCountChange(
        currentState.optimisticCountChanges,
        itemId,
        isInOtherCollections,
        1
      );

      if (currentState.activeCollectionId && originalItem) {
        const revertCache = this.buildCollectionItemsCache(
          currentState.collectionItemsCache,
          currentState.activeCollectionId,
          revertItems
        );
        setState({ collectionItemsCache: revertCache });
      }

      setState({
        activeCollectionItemIds: revertIds,
        activeCollectionItems: revertItems,
        optimisticCountChanges: revertCountChanges,
        allCollectionItemIds: revertAllItemIds,
        hasPendingChanges: false,
      });

      return { success: false, error: "Failed to remove item from favorites" };
    }
  },

  // === Preload Operations ===

  /**
   * Preloads items for a collection, using cache if available.
   * Manages loading state and handles both cache hits and misses.
   *
   * @param collectionId - Collection ID to preload
   * @param getState - Function to access current store state
   * @param setState - Function to update store state
   * @param updateActiveCollectionData - Function to update active collection in store
   * @param setItemsLoading - Function to set items loading state
   */
  async preloadCollectionItems(
    collectionId: number,
    getState: () => Pick<
      ReturnType<typeof import("@/stores/collectionsStore").useCollectionsStore.getState>,
      "collectionItemsCache" | "allCollectionItemIds" | "activeCollectionId"
    >,
    setState: (
      updates: Partial<{
        activeCollectionId: number | null;
        activeCollectionItems: SearchResultItem[];
        activeCollectionItemIds: Set<number>;
        itemsLoading: boolean;
        displayedItemsCount: number;
        hasMore: boolean;
        collectionItemsCache: Record<number, SearchResultItem[]>;
        allCollectionItemIds: Map<number, Set<number>>;
      }>
    ) => void,
    updateActiveCollectionData: (collectionId: number, items: SearchResultItem[]) => void,
    setItemsLoading: (loading: boolean) => void
  ): Promise<void> {
    const state = getState();
    const cachedItems = state.collectionItemsCache[collectionId];

    if (cachedItems !== undefined) {
      const allItemIds =
        state.allCollectionItemIds.get(collectionId) || new Set(cachedItems.map((item) => item.id));

      setState({
        activeCollectionId: collectionId,
        activeCollectionItems: cachedItems,
        activeCollectionItemIds: allItemIds,
        itemsLoading: false,
        displayedItemsCount: 20,
        hasMore: cachedItems.length > 20,
      });
    } else {
      setState({
        activeCollectionId: collectionId,
        activeCollectionItems: [],
        activeCollectionItemIds: new Set(),
        itemsLoading: true,
        displayedItemsCount: 20,
        hasMore: true,
      });
    }

    try {
      const { items, allItemIds } = await this.loadCollectionItems(collectionId, 1000, 0);

      const newState = getState();
      const newCache = this.buildCollectionItemsCache(
        newState.collectionItemsCache,
        collectionId,
        items
      );

      const newAllItemIds = this.buildAllCollectionItemIds(
        newState.allCollectionItemIds,
        collectionId,
        allItemIds
      );

      setState({
        collectionItemsCache: newCache,
        allCollectionItemIds: newAllItemIds,
        displayedItemsCount: 20,
        hasMore: items.length > 20,
      });

      const updatedState = getState();
      if (updatedState.activeCollectionId === collectionId) {
        updateActiveCollectionData(collectionId, items);
      }
    } finally {
      setItemsLoading(false);
    }
  },

  /**
   * Preloads items for a public collection (no auth required).
   * Uses cache if available, otherwise fetches from API.
   *
   * @param collectionId - Public collection ID to preload
   * @param getState - Function to access current store state
   * @param setState - Function to update store state
   * @param updateActiveCollectionData - Function to update active collection in store
   * @param setItemsLoading - Function to set items loading state
   */
  async preloadPublicCollectionItems(
    collectionId: number,
    getState: () => Pick<
      ReturnType<typeof import("@/stores/collectionsStore").useCollectionsStore.getState>,
      "collectionItemsCache" | "allCollectionItemIds" | "activeCollectionId"
    >,
    setState: (
      updates: Partial<{
        activeCollectionId: number | null;
        activeCollectionItems: SearchResultItem[];
        activeCollectionItemIds: Set<number>;
        itemsLoading: boolean;
        displayedItemsCount: number;
        hasMore: boolean;
        collectionItemsCache: Record<number, SearchResultItem[]>;
        allCollectionItemIds: Map<number, Set<number>>;
      }>
    ) => void,
    updateActiveCollectionData: (collectionId: number, items: SearchResultItem[]) => void,
    setItemsLoading: (loading: boolean) => void
  ): Promise<void> {
    const state = getState();
    const cachedItems = state.collectionItemsCache[collectionId];

    if (cachedItems !== undefined) {
      const allItemIds =
        state.allCollectionItemIds.get(collectionId) || new Set(cachedItems.map((item) => item.id));

      setState({
        activeCollectionId: collectionId,
        activeCollectionItems: cachedItems,
        activeCollectionItemIds: allItemIds,
        itemsLoading: false,
        displayedItemsCount: 20,
        hasMore: cachedItems.length > 20,
      });
    } else {
      setState({
        activeCollectionId: collectionId,
        activeCollectionItems: [],
        activeCollectionItemIds: new Set(),
        itemsLoading: true,
        displayedItemsCount: 20,
        hasMore: true,
      });
    }

    try {
      const { items, allItemIds } = await this.loadPublicCollectionItems(collectionId, 1000, 0);

      const newState = getState();
      const newCache = this.buildCollectionItemsCache(
        newState.collectionItemsCache,
        collectionId,
        items
      );

      const newAllItemIds = this.buildAllCollectionItemIds(
        newState.allCollectionItemIds,
        collectionId,
        allItemIds
      );

      setState({
        collectionItemsCache: newCache,
        allCollectionItemIds: newAllItemIds,
        displayedItemsCount: 20,
        hasMore: items.length > 20,
      });

      const updatedState = getState();
      if (updatedState.activeCollectionId === collectionId) {
        updateActiveCollectionData(collectionId, items);
      }
    } finally {
      setItemsLoading(false);
    }
  },

  /**
   * Schedules deferred preloading of all collections using requestIdleCallback or setTimeout.
   * Waits for page load before triggering preload.
   *
   * @param getState - Function to access current store state
   * @param preloadAllCollections - Function to trigger the preload operation
   */
  deferredPreloadAllCollections(
    getState: () => Pick<
      ReturnType<typeof import("@/stores/collectionsStore").useCollectionsStore.getState>,
      "collections" | "activeCollectionId"
    >,
    preloadAllCollections: () => Promise<void>
  ): void {
    const schedulePreload = () => {
      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        window.requestIdleCallback(
          () => {
            preloadAllCollections();
          },
          { timeout: 5000 }
        );
      } else {
        setTimeout(() => {
          preloadAllCollections();
        }, 2000);
      }
    };

    if (typeof window !== "undefined") {
      if (document.readyState === "complete") {
        schedulePreload();
      } else {
        window.addEventListener("load", schedulePreload, { once: true });
      }
    }
  },
};
